import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Button, Pagination } from '@it-incubator/ui-kit'
import { LoadingOverlay } from 'common/components/loading-overlay/LoadingOverlay'
import { FIRST_PAGE_NUMBER } from 'common/constants'
import { useDebounce } from 'common/hooks'
import { useGetAllPlaylistsQuery } from 'features/playlists/service'
import {
  SortDirection,
  VideoSortField,
  VideosQueries,
  useAddDaysForVideoSettingsMutation,
  useGetVideosQuery,
} from 'features/videos/service'
import { useGetCoursesQuery } from 'services/courses'
import { LOCAL_STORAGE_QUERIES_KEYS, restoreState } from 'utils'

import s from './Videos.module.css'

import { VideosHeader } from './videos-header/VideosHeader'
import { VideosTable } from './videos-table/VideosTable'

export const Videos = () => {
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(50)
  const [search, setSearch] = useState<string>('')
  const [playlistId, setPlaylistId] = useState<string>('')
  const [courseId, setCourseId] = useState<number | string>('')
  const [sortByReaction, setSortByReaction] = useState('')
  const [sortField, setSortField] = useState<VideoSortField>('default')
  const [direction, setDirection] = useState<SortDirection>('asc')
  const [settingIds, setSettingIds] = useState<string[]>([])

  const debouncedSearch = useDebounce(search, 1000)

  const videosQueries: VideosQueries = {
    filters: {
      course: courseId,
      playlist: playlistId,
      search,
    },
    sorting: {
      direction,
      sort: sortField,
      sortByReaction,
    },
  }

  const {
    data,
    isFetching,
    isLoading: isVideosLoading,
  } = useGetVideosQuery({
    course: courseId,
    direction,
    page,
    pageSize,
    playlist: playlistId,
    search: debouncedSearch,
    sort: sortField,
    sortByReaction: sortByReaction ? sortByReaction : undefined,
  })

  const { data: coursesResponse, isLoading: isCoursesLoading } = useGetCoursesQuery()
  const courses = coursesResponse?.items

  const { data: playlistsResponse, isLoading: isPlaylistsLoading } = useGetAllPlaylistsQuery({
    courseId: courseId ? String(courseId) : undefined,
  })
  const playlists = playlistsResponse?.playlists

  const [addDaysForVideoSettings, { isLoading: isAddDaysLoading }] =
    useAddDaysForVideoSettingsMutation()

  const isLoading = isVideosLoading || isPlaylistsLoading || isCoursesLoading || isAddDaysLoading

  const addDaysHandler = () => {
    addDaysForVideoSettings(settingIds)
      .unwrap()
      .then(() => {
        toast.success('Выбранные даты успешно продлены')
        setSettingIds([])
      })
      .catch(() => toast.error('Произошла ошибка'))
  }

  const perPageChangeHandler = (itemPerPage: number) => {
    setPageSize(itemPerPage)
    setPage(FIRST_PAGE_NUMBER)
  }

  useEffect(() => {
    let restoredVideosQueries = restoreState(LOCAL_STORAGE_QUERIES_KEYS.VIDEOS, videosQueries)
    const { course, playlist, search } = restoredVideosQueries.filters

    if (!restoredVideosQueries.sorting) {
      restoredVideosQueries = {
        ...videosQueries,
        sorting: { ...videosQueries.sorting },
      }
    }

    const { direction, sort, sortByReaction } = restoredVideosQueries.sorting

    if (search) {
      setSearch(search)
    }
    if (playlist) {
      setPlaylistId(playlist)
    }
    if (course) {
      setCourseId(course)
    }
    if (sortByReaction) {
      setSortByReaction(sortByReaction)
    }
    // Санитизация: раньше ключ `sort` ошибочно хранил реакцию, поэтому принимаем
    // только валидные значения нового поля сортировки.
    if (sort === 'default' || sort === 'created') {
      setSortField(sort)
    }
    if (direction === 'asc' || direction === 'desc') {
      setDirection(direction)
    }
  }, [])

  return (
    <div>
      <VideosHeader
        courseId={courseId}
        courses={courses}
        direction={direction}
        playlistId={playlistId}
        playlists={playlists}
        search={search}
        setCourseId={setCourseId}
        setDirection={setDirection}
        setPage={setPage}
        setPlaylistId={setPlaylistId}
        setSearch={setSearch}
        setSort={setSortByReaction}
        setSortField={setSortField}
        sort={sortByReaction}
        sortField={sortField}
        videosQueries={videosQueries}
      />

      <VideosTable
        courseId={courseId}
        courses={courses}
        playlistId={playlistId}
        playlists={playlists}
        setSettingIds={setSettingIds}
        settingIds={settingIds}
        sortField={sortField}
        videosData={data?.items}
      />

      <LoadingOverlay open={isFetching || isLoading} />

      <div className={s.footerContainer}>
        <Pagination
          count={Math.ceil((data?.totalCount ?? 1) / pageSize)}
          onChange={setPage}
          onPerPageChange={perPageChangeHandler}
          page={page}
          perPage={pageSize}
          perPageOptions={[10, 20, 50, 100, 500]}
        />

        <Button
          disabled={settingIds.length === 0 || isLoading}
          onClick={addDaysHandler}
          variant={'secondary'}
        >
          Продлить выбранные даты
        </Button>
      </div>
    </div>
  )
}
